import { errorMessages } from '~/constants/errorMessages.ts';
import { emptyCompletedForms } from '~/store/identification-form/initialState';
import * as Yup from 'yup';
import { ukraineOption } from './constants';
import {
  IdentificationFormSteps,
  TAddress,
  TFormAddress,
  TIdentificationStep,
  TPersonalDocument,
  TTrimValues,
} from './types';
import { citiesWithoutDistricts } from '~/constants/common.ts';
import api from '~/libs/api';
import { AddressTypes } from './forms/GeneralInfo/types';
import { EMPTY_OPTION } from '~/components/FormComponents/constants';

export const getCompleatedFormByStepName = (name: string) => {
  return (
    Object.keys(emptyCompletedForms).find((item) =>
      item.toLowerCase().includes(name?.toLowerCase())
    ) || ''
  );
};

export const getSectionFieldName = (name: string, section?: string) => {
  return `${section ? section + '.' : ''}${name}`;
};

export function checkTableField(
  this: Yup.TestContext,
  dependencyField: string,
  value?: string | null
) {
  if (!value?.trim() && this.from?.[1]?.value[dependencyField]) {
    return this.createError({
      path: this.path,
      message: errorMessages.FIELD_REQUIRED,
    });
  }
  return true;
}

export const getAddressInitialValues = (formData: { address: TAddress }) => ({
  registrationAddress: {
    address:
      formData?.address?.registrationAddress?.address ||
      generateFullAddress(formData?.address?.registrationAddress),
    country: formData?.address?.registrationAddress?.country || ukraineOption,
    postalCode: formData?.address?.registrationAddress?.postalCode || null,
    region: formData?.address?.registrationAddress?.region || EMPTY_OPTION,
    district: formData?.address?.registrationAddress?.district || EMPTY_OPTION,
    city: formData?.address?.registrationAddress?.city || EMPTY_OPTION,
    street: formData?.address?.registrationAddress?.street || EMPTY_OPTION,
    houseNumber: formData?.address?.registrationAddress?.houseNumber || null,
    building: formData?.address?.registrationAddress?.building || null,
    apartment: formData?.address?.registrationAddress?.apartment || null,
    codeAddress: formData?.address?.registrationAddress?.codeAddress || null,
  },
  permanentAddress: {
    address:
      formData?.address?.permanentAddress?.address ||
      generateFullAddress(formData?.address?.permanentAddress),
    country: formData?.address?.permanentAddress?.country || ukraineOption,
    postalCode: formData?.address?.permanentAddress?.postalCode || null,
    region: formData?.address?.permanentAddress?.region || EMPTY_OPTION,
    district: formData?.address?.permanentAddress?.district || EMPTY_OPTION,
    city: formData?.address?.permanentAddress?.city || EMPTY_OPTION,
    street: formData?.address?.permanentAddress?.street || EMPTY_OPTION,
    houseNumber: formData?.address?.permanentAddress?.houseNumber || null,
    building: formData?.address?.permanentAddress?.building || null,
    apartment: formData?.address?.permanentAddress?.apartment || null,
    codeAddress: formData?.address?.permanentAddress?.codeAddress || null,
  },
  coincidences: formData?.address?.coincidences || false,
});

export const getDocumentsInitialValues = (formData: { documents: TPersonalDocument }) => ({
  documentType: formData?.documents?.documentType || null,
  registryRecord: formData?.documents?.registryRecord ?? null,
  documentSeries: formData?.documents?.documentSeries || null,
  documentNumber: formData?.documents?.documentNumber || null,
  issueDate: formData?.documents?.issueDate || null,
  documentExpiryDate: formData?.documents?.documentExpiryDate || null,
  issuingAuthority: formData?.documents?.issuingAuthority || null,
});

export const generateFullAddress = (address: TFormAddress) => {
  if (!address) return '';
  const { country, postalCode, region, district, city, street, houseNumber, building, apartment } =
    address;

  const addressParts = [
    country?.label,
    postalCode,
    region?.label,
    district?.label,
    [
      citiesWithoutDistricts.KYIV_REGION_CODE,
      citiesWithoutDistricts.SEVASTOPOL_REGION_CODE,
    ].includes(Number(region?.value))
      ? null
      : city?.label,
    street?.label,
    houseNumber,
    building,
    apartment,
  ];

  return addressParts.filter(Boolean).join(', ');
};

export const B2fullCheck = async (appId: number | string, formId: IdentificationFormSteps) => {
  return await api.post(
    `/identification/b2-integration/full-check/application/${appId}/forms/${formId}`,
    null
  );
};

export const trimValues = (obj: TTrimValues): TTrimValues => {
  if (typeof obj === 'string') {
    return obj.trim();
  }

  if (Array.isArray(obj)) {
    return obj.map(trimValues) as Record<string, unknown>[];
  }

  if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => {
        acc[key] = trimValues(obj[key] as TTrimValues);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  return obj;
};

export const getAddressHeaders = (isSoleTrader: boolean) => ({
  [AddressTypes.registrationAddress]: 'Адреса місця реєстрації',
  [AddressTypes.permanentAddress]: isSoleTrader
    ? 'Адреса фактичного місця проживання'
    : 'Адреса фактичного місцезнаходження',
});

export const getNextStep = (
  steps: TIdentificationStep[],
  index: number,
  isStepDisabledByName: (name?: string) => boolean
): TIdentificationStep => {
  if (isStepDisabledByName(steps[index]?.name as string)) {
    return getNextStep(steps, index + 1, isStepDisabledByName);
  }
  return steps[index] as TIdentificationStep;
};
